/**
 * Annie Christmas — „Unmatched Adventures: Tales to Amaze".
 *
 * NECKLACE OF PEARLS: Annie má +2 k útoku, keď má menej zdravia než obranca.
 * Balíček ju za to nechá dobrovoľne krvácať (Lagniappe) a jednu dobu nepadnúť
 * pod 1 (Mississippi Queen). Charlie je ranged sidekick s kartovými trikmi.
 */

import { neighbors } from '../map.js';
import type { Ctx } from '../ops.js';
import { askChoice, cardDef, dealDamage, drawCard, emit, gainAction, getMap, getPlayer, healFighter } from '../ops.js';
import { areEnemies } from '../players.js';
import type { CardId, FighterId, PlayerIndex } from '../types.js';
import { askMove, cancel, emptySpacesInZone, fighterOrNull } from './helpers.js';
import type { CardEffect, EffectCtx, EffectRegistry, HeroSpecial } from './types.js';

const ANNIE = 'annie-christmas';
const CHARLIE = 'charlie';
const LAGNIAPPE = 'annie-christmas.lagniappe';
const CAPTAIN = 'annie-christmas.captains-orders';
const BOTTOM = 'annie-christmas.bottom-dealing';
const PAIR = 'annie-christmas.quite-a-pair';

const figure = (ctx: Ctx, player: PlayerIndex, defId: string) =>
  ctx.state.fighters.find((f) => f.owner === player && f.defId === defId) ?? null;

/** „Deal 2 damage to each opposing fighter adjacent to Annie." */
const aFewMorePearls: CardEffect = {
  scheme: (e) => {
    const annie = figure(e.ctx, e.player, ANNIE);
    if (!annie) return;
    const around = new Set(neighbors(getMap(e.ctx), annie.spaceId));
    const targets = e.ctx.state.fighters
      .filter((f) => areEnemies(e.ctx.state, f.owner, e.player) && around.has(f.spaceId))
      .map((f) => f.id);
    for (const id of targets) dealDamage(e.ctx, id, 2);
  },
};

/**
 * „Place Annie in any space in her zone. Then, place another friendly fighter
 * in any space in Annie's zone. Gain 1 action." Druhé položenie až po prvom —
 * Annie mohla zmeniť zónu.
 */
const captainsOrders: CardEffect = {
  scheme: (e) => {
    const annie = figure(e.ctx, e.player, ANNIE);
    if (annie) {
      askChoice(e.ctx, {
        kind: 'place',
        player: e.player,
        source: e.cardId,
        label: "Captain's Orders: polož Annie na priestor v jej zóne",
        optional: false,
        fighterId: annie.id,
        spaces: emptySpacesInZone(e.ctx, annie.id),
      });
      askChoice(e.ctx, {
        kind: 'auto',
        player: e.player,
        source: e.cardId,
        label: "Captain's Orders: ďalšia figúrka",
        optional: false,
        follow: { kind: 'effect', effect: CAPTAIN, data: 'friend' },
      });
    }
    gainAction(e.ctx, e.player);
  },
  onFollow: (ctx, choice, data) => {
    const annie = figure(ctx, choice.player, ANNIE);
    if (!annie) return;
    const friends = ctx.state.fighters.filter((f) => f.owner === choice.player && f.id !== annie.id);
    if (data === 'friend' && friends.length > 1) {
      askChoice(ctx, {
        kind: 'option',
        player: choice.player,
        source: choice.source,
        label: "Captain's Orders: ktorú figúrku položíš do Anniinej zóny?",
        optional: false,
        options: friends.map((f) => ({ label: f.name, follow: { kind: 'effect' as const, effect: CAPTAIN, data: f.id } })),
      });
      return;
    }
    const friend = data === 'friend' ? friends[0] : fighterOrNull(ctx, data);
    if (!friend) return;
    askChoice(ctx, {
      kind: 'place',
      player: choice.player,
      source: choice.source,
      label: `Captain's Orders: polož ${friend.name} na priestor v Anniinej zóne`,
      optional: false,
      fighterId: friend.id,
      spaces: emptySpacesInZone(ctx, annie.id),
    });
  },
};

/** „Move each fighter in the combat up to 2 spaces." — o oboch rozhoduje majiteľ karty. */
const keepYourHands: CardEffect = {
  after: (e) => {
    for (const id of [e.fighterId, e.opponentFighterId]) {
      if (!id) continue;
      askMove(e, { fighters: [id], max: 2, label: `Keep Your Hands to Yourself: presuň ${fighterOrNull(e.ctx, id)?.name ?? 'figúrku'} až o 2` });
    }
  },
};

/**
 * „IMMEDIATELY: Deal up to 2 damage to Annie to draw that many cards. (This
 * may cause her special ability to apply.)"
 */
const lagniappe: CardEffect = {
  immediate: (e) => {
    const annie = figure(e.ctx, e.player, ANNIE);
    if (!annie) return;
    askChoice(e.ctx, {
      kind: 'option',
      player: e.player,
      source: e.cardId,
      label: 'Lagniappe: koľko poškodenia dáš Annie? Toľko kariet potiahneš.',
      optional: false,
      options: [0, 1, 2].map((n) => ({
        label: n === 0 ? 'Nič' : `${n} poškodenie → ${n} ${n === 1 ? 'karta' : 'karty'}`,
        follow: { kind: 'effect' as const, effect: LAGNIAPPE, data: String(n) },
      })),
    });
  },
  onFollow: (ctx, choice, data) => {
    const annie = figure(ctx, choice.player, ANNIE);
    const n = Number(data);
    if (!annie || n <= 0) return;
    dealDamage(ctx, annie.id, n);
    for (let i = 0; i < n; i++) drawCard(ctx, choice.player);
  },
};

/** „IMMEDIATELY: Damage cannot reduce Annie's health below 1 this turn." */
const mississippiQueen: CardEffect = {
  immediate: (e) => {
    const annie = figure(e.ctx, e.player, ANNIE);
    if (!annie) return;
    (e.ctx.state.turn.floorOne ??= []).push(annie.id);
    emit(e.ctx, { type: 'specialUsed', player: e.player, heroId: ANNIE, name: 'Mississippi Queen: Annie tento ťah neklesne pod 1' });
  },
};

/** „Deal 1 damage to the opposing fighter. If you won the combat, deal 2 damage instead." */
const strikingBeauty: CardEffect = {
  after: (e) => {
    if (e.opponentFighterId) dealDamage(e.ctx, e.opponentFighterId, e.won ? 2 : 1);
  },
};

/**
 * „If your fighter is adjacent to a friendly fighter, your fighter and each
 * friendly fighter adjacent to them recover 1 health."
 */
const betterTogether: CardEffect = {
  after: (e) => {
    const me = fighterOrNull(e.ctx, e.fighterId);
    if (!me) return;
    const around = new Set(neighbors(getMap(e.ctx), me.spaceId));
    const friends = e.ctx.state.fighters.filter((f) => f.owner === e.player && f.id !== me.id && around.has(f.spaceId));
    if (friends.length === 0) return;
    for (const f of [me, ...friends]) healFighter(e.ctx, f.id, 1);
  },
};

/** „If the opposing fighter is not adjacent to your fighter, this card's value is 5 instead." */
const longShot: CardEffect = {
  during: (e, value) => {
    const me = fighterOrNull(e.ctx, e.fighterId);
    const them = fighterOrNull(e.ctx, e.opponentFighterId);
    if (!me || !them) return value;
    return neighbors(getMap(e.ctx), me.spaceId).includes(them.spaceId) ? value : 5;
  },
};

/** Dvojica kariet s rovnakým menom v ruke (bez karty, ktorá sa práve hrá). */
function pairInHand(ctx: Ctx, player: PlayerIndex): [CardId, CardId] | null {
  const seen = new Map<string, CardId>();
  for (const cardId of getPlayer(ctx.state, player).hand) {
    const name = cardDef(ctx, cardId).name;
    const first = seen.get(name);
    if (first) return [first, cardId];
    seen.set(name, cardId);
  }
  return null;
}

/**
 * „IMMEDIATELY: You may reveal two cards from your hand with the same name.
 * If you do, cancel all effects on your opponent's card." Odhalí sa prvá
 * nájdená dvojica — ktorá, na výsledku nezáleží.
 */
const quiteAPair: CardEffect = {
  immediate: (e) => {
    if (!pairInHand(e.ctx, e.player)) return;
    askChoice(e.ctx, {
      kind: 'confirm',
      player: e.player,
      source: e.cardId,
      label: 'Quite a Pair: ukážeš dve rovnaké karty z ruky a zrušíš efekty súperovej karty?',
      optional: true,
      effect: PAIR,
      yes: 'Ukázať pár',
      no: 'Nie',
    });
  },
  onConfirm: (ctx, choice) => {
    const pair = pairInHand(ctx, choice.player);
    const combat = ctx.state.combat;
    if (!pair || !combat) return;
    for (const cardId of pair) emit(ctx, { type: 'cardRevealed', player: choice.player, cardId });
    cancel(ctx, combat.attackerPlayer === choice.player ? 'defender' : 'attacker');
  },
};

/** „IMMEDIATELY: Annie and Charlie may swap spaces. If they do, your other fighter is now the defender." */
const slickTalker: CardEffect = {
  immediate: (e) => {
    const me = fighterOrNull(e.ctx, e.fighterId);
    if (me?.defId !== ANNIE && me?.defId !== CHARLIE) return;
    const partner = figure(e.ctx, e.player, me.defId === ANNIE ? CHARLIE : ANNIE);
    if (!partner) return;
    askChoice(e.ctx, {
      kind: 'swap',
      player: e.player,
      source: e.cardId,
      label: `Slick Talker: ${me.name} a ${partner.name} si smú vymeniť miesta — brániť sa bude ${partner.name}`,
      optional: true,
      fighterId: me.id,
      candidates: [partner.id],
    });
  },
};

/**
 * „Reveal the bottom card of your deck. Increase the value of this card by the
 * BOOST value of the revealed card, then put the revealed card on the top or
 * the bottom of your deck."
 */
const bottomDealing: CardEffect = {
  duringChoice: (e) => {
    const combat = e.ctx.state.combat;
    const deck = getPlayer(e.ctx.state, e.player).deck;
    const bottom = deck[deck.length - 1];
    if (!combat || !bottom) return;
    emit(e.ctx, { type: 'deckRevealed', player: e.player, cardId: bottom, where: 'bottom' });
    const side = e.role === 'defender' ? 'defender' : 'attacker';
    combat.boosts ??= { attacker: 0, defender: 0 };
    combat.boosts[side] += cardDef(e.ctx, bottom).boost;
    askChoice(e.ctx, {
      kind: 'option',
      player: e.player,
      source: e.cardId,
      label: `Bottom Dealing: ${cardDef(e.ctx, bottom).name} — navrch, alebo naspodok balíčka?`,
      optional: false,
      options: [
        { label: 'Navrch balíčka', follow: { kind: 'effect', effect: BOTTOM, data: `top:${bottom}` } },
        { label: 'Naspodok balíčka', follow: { kind: 'effect', effect: BOTTOM, data: `bottom:${bottom}` } },
      ],
    });
  },
  onFollow: (ctx, choice, data) => {
    const [where, cardId] = data.split(':') as ['top' | 'bottom', CardId];
    const deck = getPlayer(ctx.state, choice.player).deck;
    const index = deck.indexOf(cardId);
    if (index === -1) return;
    deck.splice(index, 1);
    if (where === 'top') {
      deck.unshift(cardId);
      emit(ctx, { type: 'cardToDeck', player: choice.player, cardId });
    } else {
      deck.push(cardId);
      emit(ctx, { type: 'cardToBottom', player: choice.player, cardId });
    }
  },
};

export const annieCards: EffectRegistry = {
  'annie-christmas.a-few-more-pearls': aFewMorePearls,
  [CAPTAIN]: captainsOrders,
  'annie-christmas.keep-your-hands-to-yourself': keepYourHands,
  [LAGNIAPPE]: lagniappe,
  'annie-christmas.mississippi-queen': mississippiQueen,
  'annie-christmas.striking-beauty': strikingBeauty,
  'annie-christmas.better-together': betterTogether,
  'annie-christmas.long-shot': longShot,
  [PAIR]: quiteAPair,
  'annie-christmas.slick-talker': slickTalker,
  [BOTTOM]: bottomDealing,
  'annie-christmas.the-turn-and-the-river': {
    after: (e: EffectCtx) => {
      drawCard(e.ctx, e.player);
      drawCard(e.ctx, e.player);
    },
  },
};

/** NECKLACE OF PEARLS — „Add +2 to the value of Annie's attacks if she has less health than the defender." */
export const annieSpecial: HeroSpecial = {
  name: 'Necklace of Pearls',
  attackModifier: (ctx, player, fighterId: FighterId | null) => {
    const annie = fighterOrNull(ctx, fighterId);
    const combat = ctx.state.combat;
    if (annie?.defId !== ANNIE || combat?.attackerPlayer !== player) return 0;
    const defender = fighterOrNull(ctx, combat.defenderFighter);
    return defender && annie.hp < defender.hp ? 2 : 0;
  },
};
